import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  Mail,
  Trash2,
  Search,
  Send,
  Eye,
  Users,
  History,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  Bell,
} from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

const DEFAULT_BODY = `<p>Gentile iscritto,</p>
<p>grazie per far parte del mondo ETERNA Motion. In questa newsletter abbiamo il piacere di condividere con te...</p>
<p>Un caro saluto,<br/>Il team ETERNA Motion</p>`;

export default function AdminNewsletterTab() {
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("subscribers"); // subscribers | compose | history
  const [loading, setLoading] = useState(true);

  // Composer
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);
  const [selected, setSelected] = useState([]); // empty = all
  const [preview, setPreview] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Notify-launch filter (subscribers view only)
  const [notifyLaunchOnly, setNotifyLaunchOnly] = useState(false);

  // Campaign detail
  const [viewingCampaign, setViewingCampaign] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [s, c, tpl] = await Promise.all([
        axios.get(`${API}/admin/newsletter/subscribers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/newsletter/campaigns`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/newsletter/templates`, { headers: getAuthHeaders() }),
      ]);
      setSubs(s.data);
      setCampaigns(c.data);
      setTemplates(tpl.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const deleteSub = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla newsletter?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
        headers: getAuthHeaders(),
      });
      setSubs((prev) => prev.filter((s) => s.email !== email));
      setSelected((prev) => prev.filter((e) => e !== email));
    } catch {
      alert("Errore nella rimozione");
    }
  };

  const toggleSelected = (email) => {
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const exportCsv = () => {
    const rows = [
      ["email", "subscribed_at", "notify_launch"],
      ...subs.map((s) => [s.email, s.subscribed_at || "", s.notify_launch ? "yes" : ""]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eterna-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cleanupTestData = async () => {
    if (!window.confirm("Vuoi rimuovere tutti gli iscritti di test (email contenenti 'test', dominio @example.com, @eterna-test.com)?\n\nQuesta operazione non puo essere annullata.")) return;
    try {
      const res = await axios.post(`${API}/admin/newsletter/cleanup-test`, {}, { headers: getAuthHeaders() });
      alert(`Rimossi ${res.data.deleted_count} iscritti di test.`);
      fetchAll();
    } catch {
      alert("Errore durante la pulizia.");
    }
  };

  const filteredSubs = subs.filter((s) => {
    if (notifyLaunchOnly && !s.notify_launch) return false;
    return s.email.toLowerCase().includes(search.toLowerCase());
  });

  const notifyLaunchCount = subs.filter((s) => s.notify_launch).length;

  const selectNotifyLaunch = () => {
    const emails = subs.filter((s) => s.notify_launch).map((s) => s.email);
    setSelected(emails);
  };

  const sendCampaign = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const payload = {
        subject,
        body_html: bodyHtml,
        recipients: selected.length > 0 ? selected : null,
      };
      const res = await axios.post(`${API}/admin/newsletter/send`, payload, {
        headers: getAuthHeaders(),
      });
      setLastResult(res.data);
      setConfirmSend(false);
      // Refresh campaigns + keep composer filled for re-use
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore durante l'invio";
      setLastResult({ error: detail });
      setConfirmSend(false);
    } finally {
      setSending(false);
    }
  };

  const targetCount = selected.length > 0 ? selected.length : subs.length;

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="animate-spin text-[#C4A462]" strokeWidth={1.25} size={28} />
      </div>
    );
  }

  return (
    <div data-testid="admin-newsletter-tab">
      {/* Sub tabs */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h2
          className="text-2xl tracking-tight text-[#1A1B1A]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
        >
          Newsletter
        </h2>
        <div className="flex items-center gap-1 bg-[#F3F1EC] p-1">
          {[
            { key: "subscribers", label: "Iscritti", icon: Users, count: subs.length },
            { key: "compose", label: "Componi & Invia", icon: Send },
            { key: "history", label: "Storico", icon: History, count: campaigns.length },
          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              data-testid={`newsletter-subtab-${key}`}
              onClick={() => setView(key)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.15em] flex items-center gap-2 transition-colors ${
                view === key ? "bg-[#1A1B1A] text-[#FAF9F6]" : "text-[#4A4A4A] hover:text-[#1A1B1A]"
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              <Icon strokeWidth={1.25} size={14} />
              {label}
              {count !== undefined && (
                <span
                  className={`text-[9px] px-1.5 py-0.5 ${
                    view === key ? "bg-[#C4A462] text-[#1A1B1A]" : "bg-[#D8D4CC]/60 text-[#4A4A4A]"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* === SUBSCRIBERS === */}
      {view === "subscribers" && (
        <div data-testid="newsletter-subscribers-view">
          <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search
                strokeWidth={1.25}
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]"
              />
              <Input
                data-testid="newsletter-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per email..."
                className="rounded-none border-[#D8D4CC] bg-white h-10 text-sm pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {filteredSubs.length} / {subs.length} iscritti
              </span>
              <button
                data-testid="newsletter-filter-notify-launch"
                onClick={() => setNotifyLaunchOnly((v) => !v)}
                title="Mostra solo iscritti che vogliono essere avvisati al lancio"
                className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 border flex items-center gap-2 transition-colors ${
                  notifyLaunchOnly
                    ? "bg-[#C4A462] border-[#C4A462] text-[#1A1B1A]"
                    : "bg-transparent border-[#D8D4CC] text-[#4A4A4A] hover:border-[#C4A462] hover:text-[#1A1B1A]"
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                <Bell strokeWidth={1.5} size={13} />
                Lancio
                <span className={`text-[9px] px-1.5 py-0.5 ${notifyLaunchOnly ? "bg-[#1A1B1A] text-[#C4A462]" : "bg-[#D8D4CC]/60 text-[#4A4A4A]"}`}>
                  {notifyLaunchCount}
                </span>
              </button>
              <button
                data-testid="newsletter-cleanup-test"
                onClick={cleanupTestData}
                className="text-[10px] uppercase tracking-[0.15em] text-[#757575] hover:text-red-600 transition-colors px-2"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                title="Rimuove iscritti di test"
              >
                Pulisci test
              </button>
              <button
                data-testid="newsletter-export-csv"
                onClick={exportCsv}
                disabled={subs.length === 0}
                className="btn-luxury-outline text-[10px] px-3 py-2 flex items-center gap-2 disabled:opacity-50"
              >
                <Download strokeWidth={1.25} size={14} />
                Export CSV
              </button>
              <button
                data-testid="newsletter-compose-cta"
                onClick={() => setView("compose")}
                className="btn-luxury text-[10px] px-3 py-2 flex items-center gap-2"
              >
                <Send strokeWidth={1.25} size={14} />
                Invia Newsletter
              </button>
            </div>
          </div>

          {filteredSubs.length === 0 ? (
            <div className="text-center py-20 border border-[#D8D4CC]/40 bg-white">
              <Mail strokeWidth={1} size={40} className="mx-auto text-[#D8D4CC] mb-4" />
              <p className="text-sm text-[#757575]">
                {subs.length === 0
                  ? "Nessun iscritto alla newsletter."
                  : "Nessun risultato per la ricerca."}
              </p>
            </div>
          ) : (
            <div className="border border-[#D8D4CC]/40 bg-white">
              <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-[#757575] border-b border-[#D8D4CC]/40 bg-[#FAF9F6]">
                <div className="col-span-7" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  Email
                </div>
                <div className="col-span-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  Iscritto il
                </div>
                <div className="col-span-1" />
              </div>
              {filteredSubs.map((s) => (
                <div
                  key={s.email}
                  data-testid={`newsletter-sub-row-${s.email}`}
                  className="grid grid-cols-12 items-center px-5 py-3 border-b border-[#D8D4CC]/20 last:border-0 hover:bg-[#FAF9F6] transition-colors"
                >
                  <div
                    className="col-span-7 text-sm text-[#1A1B1A] truncate flex items-center gap-2"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                  >
                    <span className="truncate">{s.email}</span>
                    {s.notify_launch && (
                      <span
                        data-testid={`newsletter-sub-launch-badge-${s.email}`}
                        title="Vuole essere avvisato al lancio"
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-[#C4A462] border border-[#C4A462]/50 px-1.5 py-0.5"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                      >
                        <Bell strokeWidth={1.5} size={10} />
                        Lancio
                      </span>
                    )}
                  </div>
                  <div
                    className="col-span-4 text-xs text-[#757575]"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                  >
                    {s.subscribed_at
                      ? new Date(s.subscribed_at).toLocaleDateString("it-IT", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      data-testid={`newsletter-delete-${s.email}`}
                      onClick={() => deleteSub(s.email)}
                      className="p-1.5 text-[#757575] hover:text-red-600 transition-colors"
                      aria-label={`Rimuovi ${s.email}`}
                    >
                      <Trash2 strokeWidth={1.25} size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === COMPOSE === */}
      {view === "compose" && (
        <div data-testid="newsletter-compose-view" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="border border-[#D8D4CC]/40 bg-white p-6 space-y-4">
              <div className="space-y-1">
                <Label
                  className="text-xs uppercase tracking-[0.15em] text-[#757575] flex items-center gap-2"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  <FileText strokeWidth={1.25} size={14} /> Template pre-configurato
                </Label>
                <Select
                  value=""
                  onValueChange={(id) => {
                    const tpl = templates.find((t) => t.id === id);
                    if (tpl) {
                      setSubject(tpl.subject);
                      setBodyHtml(tpl.body_html);
                    }
                  }}
                >
                  <SelectTrigger
                    data-testid="newsletter-template-select"
                    className="rounded-none border-[#D8D4CC] bg-transparent h-10 text-sm"
                  >
                    <SelectValue placeholder="Carica un template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((tpl) => (
                      <SelectItem
                        key={tpl.id}
                        value={tpl.id}
                        data-testid={`newsletter-template-${tpl.id}`}
                      >
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  className="text-[10px] text-[#757575]"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  Selezionando un template, oggetto e contenuto vengono pre-compilati. Sostituisci i segnaposto tra parentesi quadre prima di inviare.
                </p>
              </div>

              <div className="space-y-1">
                <Label
                  className="text-xs uppercase tracking-[0.15em] text-[#757575]"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  Oggetto *
                </Label>
                <Input
                  data-testid="newsletter-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Es. Nuova opera Limited Edition in arrivo"
                  className="rounded-none border-[#D8D4CC] bg-transparent h-10 text-sm"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label
                    className="text-xs uppercase tracking-[0.15em] text-[#757575]"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                  >
                    Contenuto (HTML) *
                  </Label>
                  <button
                    type="button"
                    data-testid="newsletter-preview-btn"
                    onClick={() => setPreview(true)}
                    disabled={!bodyHtml}
                    className="text-[10px] uppercase tracking-[0.15em] text-[#C4A462] hover:text-[#A98A4E] flex items-center gap-1 disabled:opacity-40"
                  >
                    <Eye strokeWidth={1.25} size={12} /> Anteprima
                  </button>
                </div>
                <Textarea
                  data-testid="newsletter-body-input"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={14}
                  className="rounded-none border-[#D8D4CC] bg-transparent text-sm resize-y font-mono"
                  placeholder="Inserisci il contenuto HTML della email..."
                />
                <p className="text-[10px] text-[#757575] mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  Suggerimento: usa CSS inline (es. <code>&lt;p style="color:#4A4A4A"&gt;</code>) per la massima compatibilità con i client email.
                </p>
              </div>

              {lastResult && (
                <div
                  data-testid="newsletter-send-result"
                  className={`p-4 border flex items-start gap-3 ${
                    lastResult.error
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-[#C4A462]/40 bg-[#FDFCF9] text-[#1A1B1A]"
                  }`}
                >
                  {lastResult.error ? (
                    <AlertCircle strokeWidth={1.5} size={18} className="mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCircle2 strokeWidth={1.5} size={18} className="mt-0.5 flex-shrink-0 text-[#C4A462]" />
                  )}
                  <div className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                    {lastResult.error ? (
                      <>Errore: {lastResult.error}</>
                    ) : (
                      <>
                        Campagna inviata. <strong>{lastResult.sent_count}/{lastResult.total_recipients}</strong> email consegnate
                        {lastResult.failed_count > 0 && ` • ${lastResult.failed_count} fallite`}.
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-[#D8D4CC]/40">
                <p className="text-xs text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  Invierò a <strong className="text-[#1A1B1A]">{targetCount}</strong>{" "}
                  {targetCount === 1 ? "iscritto" : "iscritti"}
                  {selected.length > 0 && ` (selezione personalizzata)`}.
                </p>
                <button
                  data-testid="newsletter-send-btn"
                  disabled={!subject.trim() || !bodyHtml.trim() || targetCount === 0 || sending}
                  onClick={() => setConfirmSend(true)}
                  className="btn-luxury flex items-center gap-2 disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 className="animate-spin" strokeWidth={1.25} size={16} />
                  ) : (
                    <Send strokeWidth={1.25} size={16} />
                  )}
                  Invia Newsletter
                </button>
              </div>
            </div>
          </div>

          {/* Recipients selector */}
          <div className="lg:col-span-4">
            <div className="border border-[#D8D4CC]/40 bg-white p-5 sticky top-6">
              <p
                className="text-xs uppercase tracking-[0.15em] text-[#757575] mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                Destinatari
              </p>
              <div className="flex items-center gap-2 mb-4">
                <button
                  data-testid="newsletter-select-all"
                  onClick={() => setSelected([])}
                  className={`flex-1 text-[10px] uppercase tracking-[0.15em] px-2 py-2 transition-colors ${
                    selected.length === 0
                      ? "bg-[#1A1B1A] text-[#FAF9F6]"
                      : "bg-[#F3F1EC] text-[#4A4A4A] hover:bg-[#D8D4CC]"
                  }`}
                >
                  Tutti ({subs.length})
                </button>
                <button
                  data-testid="newsletter-select-notify-launch"
                  onClick={selectNotifyLaunch}
                  disabled={notifyLaunchCount === 0}
                  title="Seleziona solo gli iscritti che vogliono essere avvisati al lancio"
                  className="flex-1 text-[10px] uppercase tracking-[0.15em] px-2 py-2 transition-colors bg-[#FDFCF9] border border-[#C4A462]/40 text-[#C4A462] hover:bg-[#C4A462] hover:text-[#1A1B1A] disabled:opacity-30 disabled:hover:bg-[#FDFCF9] disabled:hover:text-[#C4A462] flex items-center justify-center gap-1.5"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  <Bell strokeWidth={1.5} size={11} />
                  Lancio ({notifyLaunchCount})
                </button>
                <button
                  data-testid="newsletter-clear-selection"
                  onClick={() => setSelected([])}
                  disabled={selected.length === 0}
                  className="text-[10px] uppercase tracking-[0.15em] text-[#757575] hover:text-[#1A1B1A] disabled:opacity-30"
                >
                  Reset
                </button>
              </div>

              <div className="max-h-[50vh] overflow-y-auto border border-[#D8D4CC]/40">
                {subs.length === 0 ? (
                  <p className="text-xs text-[#757575] p-4 text-center">Nessun iscritto disponibile.</p>
                ) : (
                  subs.map((s) => {
                    const checked = selected.length === 0 || selected.includes(s.email);
                    return (
                      <label
                        key={s.email}
                        data-testid={`newsletter-recipient-${s.email}`}
                        className="flex items-center gap-2 px-3 py-2 border-b border-[#D8D4CC]/20 last:border-0 cursor-pointer hover:bg-[#FAF9F6]"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelected(s.email)}
                          className="accent-[#C4A462]"
                        />
                        <span
                          className="text-xs text-[#4A4A4A] truncate"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {s.email}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === HISTORY === */}
      {view === "history" && (
        <div data-testid="newsletter-history-view" className="space-y-3">
          {campaigns.length === 0 ? (
            <div className="text-center py-20 border border-[#D8D4CC]/40 bg-white">
              <History strokeWidth={1} size={40} className="mx-auto text-[#D8D4CC] mb-4" />
              <p className="text-sm text-[#757575]">Nessuna campagna inviata.</p>
            </div>
          ) : (
            campaigns.map((c) => (
              <div
                key={c.id}
                data-testid={`campaign-${c.id}`}
                className="border border-[#D8D4CC]/40 bg-white p-5 flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-lg tracking-tight text-[#1A1B1A] truncate"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                  >
                    {c.subject}
                  </h3>
                  <p className="text-xs text-[#757575] mt-1">
                    {c.sent_at
                      ? new Date(c.sent_at).toLocaleDateString("it-IT", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}{" "}
                    • {c.sent_by || ""}
                  </p>
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-xs text-[#C4A462]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      ✓ {c.sent_count}/{c.total_recipients} inviate
                    </span>
                    {c.failed_count > 0 && (
                      <span className="text-xs text-red-600">✕ {c.failed_count} fallite</span>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`campaign-view-${c.id}`}
                  onClick={() => setViewingCampaign(c)}
                  className="btn-luxury-outline text-[10px] px-3 py-2 flex items-center gap-2 flex-shrink-0"
                >
                  <Eye strokeWidth={1.25} size={14} /> Dettagli
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent
          data-testid="newsletter-preview-dialog"
          className="bg-[#FAF9F6] border-[#D8D4CC] rounded-none max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle
              className="text-xl tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Anteprima Email
            </DialogTitle>
            <DialogDescription className="text-xs text-[#757575]">
              {subject || "(nessun oggetto)"}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 border border-[#D8D4CC] bg-white p-6">
            <div
              className="prose prose-sm max-w-none text-[#1A1B1A]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml) }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Send Dialog */}
      <Dialog open={confirmSend} onOpenChange={(o) => !sending && setConfirmSend(o)}>
        <DialogContent
          data-testid="newsletter-confirm-dialog"
          className="bg-[#FAF9F6] border-[#D8D4CC] rounded-none max-w-md"
        >
          <DialogHeader>
            <DialogTitle
              className="text-xl tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Confermi l'invio?
            </DialogTitle>
            <DialogDescription className="text-sm text-[#4A4A4A]">
              Stai per inviare la newsletter "<strong>{subject}</strong>" a{" "}
              <strong>{targetCount}</strong> {targetCount === 1 ? "iscritto" : "iscritti"}.
              L'operazione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 mt-6">
            <button
              data-testid="newsletter-confirm-cancel"
              onClick={() => setConfirmSend(false)}
              disabled={sending}
              className="btn-luxury-outline flex-1 disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              data-testid="newsletter-confirm-send"
              onClick={sendCampaign}
              disabled={sending}
              className="btn-luxury flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin" strokeWidth={1.25} size={14} />
                  Invio...
                </>
              ) : (
                <>
                  <Send strokeWidth={1.25} size={14} />
                  Conferma Invio
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail */}
      <Dialog open={!!viewingCampaign} onOpenChange={(o) => !o && setViewingCampaign(null)}>
        <DialogContent
          data-testid="campaign-detail-dialog"
          className="bg-[#FAF9F6] border-[#D8D4CC] rounded-none max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          {viewingCampaign && (
            <>
              <DialogHeader>
                <DialogTitle
                  className="text-xl tracking-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                >
                  {viewingCampaign.subject}
                </DialogTitle>
                <DialogDescription className="text-xs text-[#757575]">
                  {viewingCampaign.sent_at
                    ? new Date(viewingCampaign.sent_at).toLocaleString("it-IT")
                    : ""}{" "}
                  • Da: {viewingCampaign.from}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 border border-[#D8D4CC]/40 bg-white">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#757575]">Totale</p>
                    <p
                      className="text-2xl text-[#1A1B1A] mt-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {viewingCampaign.total_recipients}
                    </p>
                  </div>
                  <div className="p-3 border border-[#C4A462]/30 bg-[#FDFCF9]">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#C4A462]">Inviate</p>
                    <p
                      className="text-2xl text-[#C4A462] mt-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {viewingCampaign.sent_count}
                    </p>
                  </div>
                  <div className="p-3 border border-red-200 bg-red-50">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-red-700">Fallite</p>
                    <p
                      className="text-2xl text-red-700 mt-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {viewingCampaign.failed_count}
                    </p>
                  </div>
                </div>
                <div className="border border-[#D8D4CC] bg-white p-5">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#757575] mb-3">Contenuto</p>
                  <div
                    className="prose prose-sm max-w-none text-[#1A1B1A]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingCampaign.body_html) }}
                  />
                </div>
                {viewingCampaign.failed_recipients && viewingCampaign.failed_recipients.length > 0 && (
                  <div className="border border-red-200 bg-red-50 p-4">
                    <p className="text-xs uppercase tracking-[0.15em] text-red-700 mb-2">
                      Email non consegnate ({viewingCampaign.failed_recipients.length})
                    </p>
                    <div className="text-xs text-red-700 space-y-1 max-h-40 overflow-y-auto">
                      {viewingCampaign.failed_recipients.map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
